/**
 * AppActionContext — web port (RN-agnostic, copy of RN logic).
 *
 * Unified action dispatch for deep links, Socket.IO events, push, and
 * internal navigation. Sources push actions; consumers (Dashboard) react.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type AppAction =
  | {
      type: 'open-drawer';
      drawer:
        | 'trip'
        | 'explore'
        | 'book'
        | 'deals'
        | 'inbox'
        | 'chat'
        | 'account';
      mode?: string;
      query?: string;
      category?: string;
      dayIndex?: number;
      itemId?: string;
    }
  | {
      type: 'show-map';
      markers?: Array<{
        latitude: number;
        longitude: number;
        title: string;
        description?: string;
      }>;
      region?: {
        latitude: number;
        longitude: number;
        latitudeDelta: number;
        longitudeDelta: number;
      };
      title?: string;
    }
  | { type: 'show-webview'; url: string; title?: string }
  | {
      type: 'show-paywall';
      offeringType?: string;
      source?: string;
      context?: Record<string, any>;
    }
  | { type: 'show-deal'; dealId: string }
  | { type: 'trip-review'; requestId: string; itineraryId?: string }
  | { type: 'show-account' }
  | { type: 'show-item-detail'; itemId: string; itemType?: string }
  | { type: 'navigate'; screen: string; params?: Record<string, any> };

interface AppActionContextValue {
  dispatch: (action: AppAction) => void;
  pendingAction: AppAction | null;
  consumeAction: () => void;
}

const AppActionContext = createContext<AppActionContextValue>({
  dispatch: () => {},
  pendingAction: null,
  consumeAction: () => {},
});

export const useAppAction = () => useContext(AppActionContext);

export const AppActionProvider = ({ children }: { children: ReactNode }) => {
  const [pendingAction, setPendingAction] = useState<AppAction | null>(null);
  const queueRef = useRef<AppAction[]>([]);

  const consumeAction = useCallback(() => {
    setPendingAction(null);
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift()!;
      setTimeout(() => setPendingAction(next), 100);
    }
  }, []);

  const dispatch = useCallback(
    (action: AppAction) => {
      if (pendingAction) queueRef.current.push(action);
      else setPendingAction(action);
    },
    [pendingAction],
  );

  return (
    <AppActionContext.Provider
      value={{ dispatch, pendingAction, consumeAction }}
    >
      {children}
    </AppActionContext.Provider>
  );
};

/**
 * Parse a deep-link URL into an AppAction. Web's analogue handles both the
 * custom `wunderguide://action/...` scheme and `https://go.wunderguide.com/action/...`
 * universal links. Returns null if not an action URL.
 */
export function parseDeepLinkAction(url: string): AppAction | null {
  try {
    const normalized = url
      .replace('wunderguide://action/', '')
      .replace('https://go.wunderguide.com/action/', '');

    if (normalized === url) return null;

    const [path, queryString] = normalized.split('?');
    const type = path.replace(/\/$/, '');
    const params: Record<string, string> = {};

    if (queryString) {
      for (const pair of queryString.split('&')) {
        const [key, value] = pair.split('=');
        if (key && value)
          params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }

    switch (type) {
      case 'open-drawer':
        return {
          type: 'open-drawer',
          drawer: (params.drawer as any) || 'trip',
          mode: params.mode,
          query: params.query,
          category: params.category,
          dayIndex: params.dayIndex ? parseInt(params.dayIndex, 10) : undefined,
          itemId: params.itemId,
        };
      case 'show-map':
        return {
          type: 'show-map',
          title: params.title,
          markers: params.markers ? JSON.parse(params.markers) : undefined,
          region: params.region ? JSON.parse(params.region) : undefined,
        };
      case 'show-webview':
        return {
          type: 'show-webview',
          url: params.url || '',
          title: params.title,
        };
      case 'show-paywall':
        return {
          type: 'show-paywall',
          offeringType: params.offeringType,
          source: params.source,
        };
      case 'show-deal':
        return { type: 'show-deal', dealId: params.dealId || params.id || '' };
      case 'trip-review':
        return {
          type: 'trip-review',
          requestId: params.requestId || params.id || '',
          itineraryId: params.itineraryId,
        };
      case 'show-account':
        return { type: 'show-account' };
      case 'show-item-detail':
        return {
          type: 'show-item-detail',
          itemId: params.itemId || params.id || '',
          itemType: params.itemType,
        };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export default AppActionContext;
